import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { api, errorText } from "../../lib/api";
import { fromIso, humanDate, toIso } from "../forms/SlotPicker";
import { LeadDetail, statusLabel } from "./LeadDetail";

const TONE = { confirme: "bg-emerald-50 text-emerald-800 ring-emerald-200", "en-attente": "bg-amber-50 text-amber-800 ring-amber-200", refuse: "bg-slate-100 text-slate-500 ring-slate-200 line-through", annule: "bg-slate-100 text-slate-500 ring-slate-200 line-through" };
const addDays = (iso, n) => { const d = fromIso(iso); d.setDate(d.getDate() + n); return toIso(d); };
const monday = (iso) => { const d = fromIso(iso); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return toIso(d); };

function Appt({ a, onOpen }) {
  return (
    <button onClick={() => onOpen(a.id)} className={`w-full rounded-xl p-3 text-left text-sm ring-1 transition-shadow hover:shadow-md ${TONE[a.status] || "bg-white text-ag-ink ring-slate-200"}`} data-testid={`planning-appt-${a.id}`}>
      <span className="flex items-center justify-between gap-2"><span className="font-mono font-bold">{a.creneau.replace(":", "h")}</span><span className="text-[11px] font-semibold uppercase">{statusLabel(a.status)}</span></span>
      <span className="mt-1 block font-semibold">{a.nom} {a.prenom || ""}</span>
      <span className="block text-xs opacity-80">{[a.service, [a.marque, a.modele].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</span>
      <a href={`tel:${a.telephone}`} onClick={(e) => e.stopPropagation()} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-ag-blue"><Phone className="h-3 w-3" />{a.telephone}</a>
    </button>
  );
}

function DayColumn({ day, appts, blocks, onOpen }) {
  const dayBlocks = blocks.filter((b) => b.date === day);
  const active = appts.filter((a) => !["refuse", "annule"].includes(a.status)).length;
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200" data-testid={`planning-day-${day}`}>
      <p className="font-display font-bold text-ag-ink first-letter:uppercase">{humanDate(day)}</p>
      <p className="mb-3 text-xs text-slate-500">{active} rendez-vous</p>
      <div className="space-y-2">
        {dayBlocks.map((b) => <p key={b.id} className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white">Bloqué · {b.time ? b.time.replace(":", "h") : "toute la journée"}{b.reason ? ` · ${b.reason}` : ""}</p>)}
        {appts.length === 0 && !dayBlocks.length && <p className="text-sm text-slate-400">Aucun rendez-vous.</p>}
        {appts.map((a) => <Appt key={a.id} a={a} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

export function PlanningTab() {
  const [view, setView] = useState("day");
  const [date, setDate] = useState(() => toIso(new Date()));
  const [data, setData] = useState({ appointments: [], blocks: [] });
  const [open, setOpen] = useState(null);
  const start = view === "week" ? monday(date) : date;
  const days = view === "week" ? 6 : 1;
  const load = () => api.get("/admin/planning", { params: { start, days } })
    .then((r) => setData({ appointments: Array.isArray(r.data?.appointments) ? r.data.appointments : [], blocks: Array.isArray(r.data?.blocks) ? r.data.blocks : [] }))
    .catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); }, [start, days]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = Array.from({ length: days }, (_, i) => addDays(start, i));
  const step = view === "week" ? 7 : 1;
  return (
    <div data-testid="planning-tab">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {[["day", "Jour"], ["week", "Semaine"]].map(([k, l]) => <button key={k} onClick={() => setView(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${view === k ? "bg-ag-navy text-white" : "bg-white ring-1 ring-slate-200"}`} data-testid={`planning-view-${k}`}>{l}</button>)}
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          <button onClick={() => setDate(addDays(date, -step))} className="grid h-9 w-9 place-items-center rounded-full bg-white ring-1 ring-slate-200" aria-label="Précédent" data-testid="planning-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setDate(toIso(new Date()))} className="rounded-full bg-white px-4 py-1.5 text-sm font-semibold ring-1 ring-slate-200" data-testid="planning-today">Aujourd'hui</button>
          <button onClick={() => setDate(addDays(date, step))} className="grid h-9 w-9 place-items-center rounded-full bg-white ring-1 ring-slate-200" aria-label="Suivant" data-testid="planning-next"><ChevronRight className="h-4 w-4" /></button>
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="field w-auto min-w-0 max-w-[170px] py-1.5" data-testid="planning-date" />
        </div>
      </div>
      <p className="mb-3 text-sm text-slate-600">Rappel automatique : la veille à 17h, un email est envoyé aux clients dont le rendez-vous est confirmé.</p>
      <div className={view === "week" ? "grid gap-3 md:grid-cols-2 xl:grid-cols-3" : "max-w-2xl"}>
        {list.map((d) => <DayColumn key={d} day={d} appts={data.appointments.filter((a) => a.date_souhaitee === d)} blocks={data.blocks} onOpen={setOpen} />)}
      </div>
      <LeadDetail id={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
